import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardBody, Button, Collapse, Row, Col } from 'reactstrap';
import api from '../../services/api';
import LeagueScheduleTimeslots from './LeagueScheduleTimeslots';
import LeagueScheduleDates from './LeagueScheduleDates';
import NewLeagueScheduleModal from './NewLeagueScheduleModal';
import EditLeagueScheduleModal from './EditLeagueScheduleModal';

const LeagueSchedules = ({ league }) => {
    const [schedules, setSchedules] = useState([]);
    const [newScheduleModal, setNewScheduleModal] = useState(false);
    const [editScheduleModal, setEditScheduleModal] = useState(false);
    const [editingSchedule, setEditingSchedule] = useState(null);
    const [expandedSchedules, setExpandedSchedules] = useState({});

    const toggleNewModal = () => setNewScheduleModal(!newScheduleModal);
    const toggleEditModal = () => setEditScheduleModal(!editScheduleModal);

    const toggleScheduleExpand = (scheduleId) => {
        setExpandedSchedules(prev => ({
            ...prev,
            [scheduleId]: !prev[scheduleId]
        }));
    };

    const fetchSchedules = useCallback(async () => {
        try {
            const response = await api.get(`/league-schedules/?league=${league.id}`);
            setSchedules(response.data);
        } catch (error) {
            console.error('Error fetching league schedules:', error);
        }
    }, [league.id]);

    useEffect(() => {
        if (league?.id) {
            fetchSchedules();
        }
    }, [league?.id, fetchSchedules]);

    const handleAddSchedule = async (scheduleData) => {
        try {
            await api.post('/league-schedules/', scheduleData);
            fetchSchedules();
            toggleNewModal();
        } catch (error) {
            console.error('Error adding schedule:', error);
            alert('Error adding schedule: ' + (error.response?.data?.detail || error.message));
        }
    };

    const handleEditSchedule = (schedule) => {
        setEditingSchedule(schedule);
        toggleEditModal();
    };

    const handleSaveSchedule = async (scheduleData) => {
        try {
            await api.put(`/league-schedules/${editingSchedule.id}/`, scheduleData);
            fetchSchedules();
            toggleEditModal();
            setEditingSchedule(null);
        } catch (error) {
            console.error('Error updating schedule:', error);
            alert('Error updating schedule: ' + (error.response?.data?.detail || error.message));
        }
    };

    const handleDeleteSchedule = async (scheduleId) => {
        if (!window.confirm('Are you sure you want to delete this schedule? This will delete all associated timeslots and dates.')) {
            return;
        }
        try {
            await api.delete(`/league-schedules/${scheduleId}/`);
            fetchSchedules();
        } catch (error) {
            console.error('Error deleting schedule:', error);
            alert('Error deleting schedule: ' + (error.response?.data?.detail || error.message));
        }
    };

    if (!league?.id) {
        return <p className="text-muted small">Select a league to manage its schedules.</p>;
    }

    return (
        <>
            <Card className="mt-3">
                <CardBody className="pt-3">
                    {schedules.length === 0 ? (
                        <p className="text-muted small">No schedules configured for this league.</p>
                    ) : (
                        <Row className="g-2">
                            {schedules.map((schedule) => (
                                <Col key={schedule.id} xs={12}>
                                    <Card className="border-primary">
                                        <CardHeader
                                            onClick={() => toggleScheduleExpand(schedule.id)}
                                            className="cursor-pointer d-flex justify-content-between align-items-center"
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <span className="fw-semibold">
                                                {expandedSchedules[schedule.id] ? '▼' : '▶'} {schedule.title}
                                            </span>
                                            <div onClick={(e) => e.stopPropagation()}>
                                                <Button
                                                    color="primary"
                                                    size="sm"
                                                    onClick={() => handleEditSchedule(schedule)}
                                                >
                                                    Edit
                                                </Button>{' '}
                                                <Button
                                                    color="danger"
                                                    size="sm"
                                                    onClick={() => handleDeleteSchedule(schedule.id)}
                                                >
                                                    Delete
                                                </Button>
                                            </div>
                                        </CardHeader>

                                        <Collapse isOpen={expandedSchedules[schedule.id]}>
                                            <CardBody className="p-2">
                                                <LeagueScheduleTimeslots schedule={schedule} serviceDuration={league?.service?.duration} />
                                                <LeagueScheduleDates schedule={schedule} leagueId={league.id} />
                                            </CardBody>
                                        </Collapse>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    )}
                    <div className="mt-3">
                        <Button color="success" size="sm" onClick={toggleNewModal}>
                            + Add Schedule
                        </Button>
                    </div>
                </CardBody>
            </Card>

            <NewLeagueScheduleModal
                isOpen={newScheduleModal}
                toggle={toggleNewModal}
                onSave={handleAddSchedule}
                leagueId={league?.id}
            />

            <EditLeagueScheduleModal
                isOpen={editScheduleModal}
                toggle={toggleEditModal}
                onSave={handleSaveSchedule}
                schedule={editingSchedule}
            />
        </>
    );
};

export default LeagueSchedules;
